import { ExpectedDeliveryDateViewModel } from '../../Purchase/Checkout/Delivery/DeliveryMethods/Common/ExpectedDelivery/ExpectedDeliveryDateViewModel.csharp';
import { LocalStorageAccessor } from '../../StorageAccessor/LocalStorageAccessor';

export interface ExpectedDeliveryCacheModel {
    postalCode: string | null;
    methods: ExpectedDeliveryDateViewModel[];
}

interface MethodsLocalStorageObject extends ExpectedDeliveryCacheModel {
    expirationDate: Date;
}

export class ExpectedDeliveryDateCache {
    private readonly postalCodeKey = 'expectedDeliveryDate:postalCode';
    private readonly selectedMethodKey = 'expectedDeliveryDate:selectedMethod';
    private readonly methodsKey = 'expectedDeliveryDate:methods';
    private readonly cacheValidTime = 10 * 60 * 1000;

    public savePostalCode = (postalCode: string) => {
        LocalStorageAccessor.setItem(this.postalCodeKey, postalCode);
    };

    public getPostalCode = (): string | null => {
        return LocalStorageAccessor.getItem(this.postalCodeKey) || null;
    };

    public saveSelectedMethod = (selectedMethod: ExpectedDeliveryDateViewModel) => {
        LocalStorageAccessor.setItem(this.selectedMethodKey, JSON.stringify(selectedMethod));
    };

    public removeSelectedMethod = () => {
        LocalStorageAccessor.removeItem(this.selectedMethodKey);
    };

    public getSelectedMethod = (): ExpectedDeliveryDateViewModel | null => {
        const selectedMethod = LocalStorageAccessor.getItem(this.selectedMethodKey) || null;

        if (selectedMethod) return JSON.parse(selectedMethod) as ExpectedDeliveryDateViewModel;

        return null;
    };

    public saveMethods = (model: ExpectedDeliveryCacheModel) => {
        const methodsObject: MethodsLocalStorageObject = {
            ...model,
            expirationDate: this.getCacheExpirationDate(model.methods)
        };

        LocalStorageAccessor.setItem(this.methodsKey, JSON.stringify(methodsObject));
    };

    public getMethods = (): ExpectedDeliveryCacheModel | null => {
        const methodsString = LocalStorageAccessor.getItem(this.methodsKey);

        if (!methodsString) return null;

        const methodsObject = JSON.parse(methodsString) as MethodsLocalStorageObject;

        if (new Date(methodsObject.expirationDate) < new Date()) {
            LocalStorageAccessor.removeItem(this.methodsKey);
            return null;
        }

        return methodsObject;
    };

    private getCacheExpirationDate = (methods: ExpectedDeliveryDateViewModel[]) => {
        const upcomingCutOffTimes = methods
            .filter((x) => x.cutOffTime !== null)
            .map((x) => {
                const date = new Date();
                const timeParts = x.cutOffTime.split(':');
                date.setHours(parseInt(timeParts[0]));
                date.setMinutes(parseInt(timeParts[1]));
                date.setSeconds(0);
                return date.getTime();
            })
            .filter((x) => x > new Date().getTime())
            .sort((a, b) => a - b);

        const firstUpcomingCutOffTime = upcomingCutOffTimes ? upcomingCutOffTimes[0] : null;
        const defaultCacheExpirationTime = new Date(new Date().getTime() + this.cacheValidTime);

        if (firstUpcomingCutOffTime) {
            const soonerDate = Math.min(firstUpcomingCutOffTime, defaultCacheExpirationTime.getTime());
            return new Date(soonerDate);
        }

        return defaultCacheExpirationTime;
    };
}
